/**
 * Client-Side General Files API
 *
 * `directApiClient`-backed helpers for general (non-matter-scoped) file
 * mutations. Replaces the mutation server actions retired in LF-3217.
 *
 * Reads (`getFileAction`, `downloadFileAction`) and the upload-orchestration
 * actions (`getPresignedUrlAction`, `uploadCompleteAction`) remain server
 * actions — see `./CLAUDE.md`.
 */

import { directApiClient } from '@/lib/api/direct-client';
import {
  FileEntity,
  UpdateFileRequest,
  UpdateFileRequestSchema
} from './types';

export class GeneralFileValidationError extends Error {
  constructor(
    message: string,
    public readonly validationErrors?: Record<string, string[] | undefined>
  ) {
    super(message);
    this.name = 'GeneralFileValidationError';
  }
}

/**
 * Update file metadata
 */
export async function updateFile(
  fileId: number,
  data: UpdateFileRequest
): Promise<FileEntity> {
  const validation = UpdateFileRequestSchema.safeParse(data);
  if (!validation.success) {
    throw new GeneralFileValidationError(
      'Validation failed',
      validation.error.flatten().fieldErrors
    );
  }

  return directApiClient.put<FileEntity>(`/files/${fileId}`, validation.data);
}

/**
 * Delete a file (soft delete)
 */
export async function deleteFile(fileId: number): Promise<void> {
  await directApiClient.delete(`/files/${fileId}`);
}

export const generalFilesApi = {
  updateFile,
  deleteFile
};
